import {AbstractContent} from '../components/abstract-content';
import {HyphenopolyUtility} from '../utilities/hyphenopoly-utility';
import {DataApiResult} from '../types/data-api';

// Settings to define a plugin. These settings can be set via a plugin's FlexForm, or on demand.
interface PluginSettings {
  sorts: string | null,
  silent: boolean,
  start: number,
  startingPoint: number,
  itemsPerPage: number,
  itemClass: string,
  layout: number,
  limit: number,
  uids: number[],
  excludeUids: number[],
  expertises: number[],
  lawyers: number[],
  positions: number[],
  preserveOrder: string[],
  boost: { [key: number]: { [key: string | number]: string | number } }
}

// Filter for a plugin list. As opposed to the plugin settings, these filters will be saved in the user's local storage.
interface PluginFilter {
  text: string,
  expertise: number,
  lawyer: number,
  position: number,
  year: number,
  category: number
}

export {PluginSettings, PluginFilter};

/**
 * Base for a plugin implementing the data API.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export abstract class AbstractDataApiPlugin<T extends PluginSettings> extends AbstractContent<T> {
  protected placeholderContainer: HTMLDivElement | null;
  protected resultContainer: HTMLDivElement | null;
  protected noResultContainer: HTMLDivElement | null;

  constructor(container: HTMLElement) {
    super(container);

    // Parse plugin settings and set default values for all plugins
    this.setSettingsFromElement(this.container);
    this.settings.start = 0;

    this.placeholderContainer = container.querySelector<HTMLDivElement>('[data-placeholder]');
    this.resultContainer = container.querySelector<HTMLDivElement>('[data-result]');
    this.noResultContainer = container.querySelector<HTMLDivElement>('[data-no-result]');
  }

  /**
   * Removes the placeholder container, if existing.
   */
  private removePlaceholderContainer(): void {
    this.placeholderContainer?.remove();
  }

  /**
   * Checks if the data API result was empty.
   *
   * @param {DataApiResult<any>} result Data API result
   * @returns {boolean} True if the result was empty, false otherwise
   */
  protected isResultEmpty(result: DataApiResult<any>): boolean {
    return result.numFound === 0;
  }

  /**
   * This method should be called if a data API result was not empty. Makes the container and a spacer afterwards
   * visible.
   */
  protected notEmpty(): void {
    this.container.classList.remove('d-none');
    if (this.noResultContainer) {
      this.resultContainer?.classList.remove('d-none');
      this.noResultContainer?.classList.add('d-none');
    }
  }

  /**
   * This method should be called if a data API result was empty. May show info messages to the user.
   */
  protected empty(): void {
    if (this.settings.silent) {
      this.container.classList.add('d-none');
    } else if (this.noResultContainer) {
      this.container.classList.remove('d-none');
      this.resultContainer?.classList.add('d-none');
      this.noResultContainer?.classList.remove('d-none');
    }
  }

  /**
   * This method should be called if a data API error occurred. May show info messages to the user.
   *
   * @param {Error} e Error
   */
  protected error(e: Error): void {
    console.error(e);
    if (this.settings.silent) {
      this.container.classList.add('d-none');
    } else {
      this.container.classList.remove('d-none');
      this.resultContainer?.classList.add('d-none');
      this.noResultContainer?.classList.remove('d-none');
    }
  }

  /**
   * Updates the result container with some content, after everything was deemed OK.
   *
   * @param {string} html HTML contents
   */
  protected update(html: string): void {
    this.removePlaceholderContainer();
    if (!this.resultContainer) {
      return;
    }
    this.container.classList.remove('d-none');
    this.resultContainer.innerHTML = html;
    this.resultContainer.classList.remove('d-none');
    HyphenopolyUtility.hyphenate(this.resultContainer);
  }

  /**
   * Shows an info with some message.
   *
   * @param {string} message Message to show
   * @param {string} [title] Message title
   */
  protected showInfo(message: string, title: string = ''): void {
    this.showAlert(message, title, 'info');
  }

  /**
   * Shows a warning with some message.
   *
   * @param {string} message Message to show
   * @param {string} [title] Message title
   */
  protected showWarning(message: string, title: string = ''): void {
    this.showAlert(message, title, 'warning');
  }

  /**
   * Shows an error with some message.
   *
   * @param {string} message Message to show
   * @param {string} [title] Message title
   */
  protected showError(message: string, title: string = ''): void {
    this.showAlert(message, title, 'danger');
  }

  /**
   * Shows an alert.
   *
   * @param {string} message Message to show
   * @param {string} [title] Message title
   * @param {string} severity Severity
   * @private
   */
  private showAlert(message: string, title: string = '', severity: string = 'info'): void {
    this.removePlaceholderContainer();
    if (!this.resultContainer) {
      return;
    }
    if (title !== '') {
      title = `<h4 class="alert-heading">${title}</h4>`;
    }
    this.resultContainer.innerHTML = `<div class="container">
    <div class="row">
        <div class="col">
            <div role="alert" class="alert alert-${severity}">
                ${title}
                <p class="mb-0">${message}</p>
            </div>
        </div>
    </div>
</div>
`;
    this.container.classList.remove('d-none');
    this.resultContainer.classList.remove('d-none');
  }
}
